import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {GeometryAttributeData, GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {Operators} from "#template/geometry-processing/geometry-graph"
import type {AttributeRefSet} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import {
    applyGeometryOperator,
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    geometryAttributeRefDataSetToGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

// Cross-dim operator: source dim slot is `TSourceAttribute`; target dim slots are `TTargetAttribute`
// and `TTargetAttributeSet`. The output dim matches the source dim and is bound by the variant's
// class generic.
export type ProjectOperatorParameters<TSourceAttribute, TTargetAttribute, TTargetAttributeSet> = {
    name: string
    sourceAttribute: TSourceAttribute | null
    targetAttribute: TTargetAttribute | null
    targetAttributeSet: TTargetAttributeSet | null
}

export const projectOperatorParametersSchema = <TSourceAttribute, TTargetAttribute, TTargetAttributeSet>(
    sourceAttributeSchema: z.ZodMiniType<TSourceAttribute, TSourceAttribute>,
    targetAttributeSchema: z.ZodMiniType<TTargetAttribute, TTargetAttribute>,
    targetAttributeSetSchema: z.ZodMiniType<TTargetAttributeSet, TTargetAttributeSet>,
) =>
    z.object({
        name: z.string(),
        sourceAttribute: z.nullable(sourceAttributeSchema),
        targetAttribute: z.nullable(targetAttributeSchema),
        targetAttributeSet: z.nullable(targetAttributeSetSchema),
    })

function applyProjectOperator<SourceDim extends IndexingDim, TargetDim extends IndexingDim>(
    sourceAttribute: GeometryAttributeData<SourceDim>,
    targetAttribute: GeometryAttributeData<TargetDim>,
    targetAttributeSet: GeometryAttributeSetData<TargetDim>,
): GeometryAttributeSetData<SourceDim> {
    const sourceAttrRefData = sourceAttribute.ref
    const targetAttrRefData = targetAttribute.ref

    const targetAttrRefDataSet = geometryAttributeSetDataToGeometryAttributeRefDataSet(targetAttributeSet)

    const projectedAttrRefDataSet = applyGeometryOperator(
        (sourceAttrRef: AttributeRef, targetAttrRef: AttributeRef, targetAttrRefSet: AttributeRefSet) =>
            Operators.project(sourceAttrRef, {sourceAttrRef}, targetAttrRef, targetAttrRefSet),
        sourceAttrRefData,
        targetAttrRefData,
        targetAttrRefDataSet,
    )

    return geometryAttributeRefDataSetToGeometryAttributeSetData(projectedAttrRefDataSet, sourceAttribute.dim)
}

export abstract class GeometryProjectOperatorNode<
    out T extends IndexingDim,
    out TSourceAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
    out TTargetAttribute extends EvaluableTemplateNode<GeometryAttributeData<IndexingDim> | null>,
    out TTargetAttributeSet extends EvaluableTemplateNode<GeometryAttributeSetData<IndexingDim> | null>,
> extends GeometryAttributeSetNode<T, ProjectOperatorParameters<TSourceAttribute, TTargetAttribute, TTargetAttributeSet>> {
    protected override hasValidParameters(
        parameters: ProjectOperatorParameters<TSourceAttribute, TTargetAttribute, TTargetAttributeSet>,
    ): parameters is ProjectOperatorParameters<TSourceAttribute, TTargetAttribute, TTargetAttributeSet> &
        NonNullableParameters<ProjectOperatorParameters<TSourceAttribute, TTargetAttribute, TTargetAttributeSet>> {
        return parameters.sourceAttribute !== null && parameters.targetAttribute !== null && parameters.targetAttributeSet !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<ProjectOperatorParameters<TSourceAttribute, TTargetAttribute, TTargetAttributeSet>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {sourceAttribute, targetAttribute, targetAttributeSet} = parameters

        const [sourceAttributeDataValid, sourceAttributeDataInvalid] = scope.branch(evaluator.evaluate(sourceAttribute))
        const [targetAttributeDataValid, targetAttributeDataInvalid] = scope.branch(evaluator.evaluate(targetAttribute))
        const [targetAttributeSetDataValid, targetAttributeSetDataInvalid] = scope.branch(evaluator.evaluate(targetAttributeSet))

        const geometryAttributeSetData = scope.pureLambda(
            scope.tuple(sourceAttributeDataValid, targetAttributeDataValid, targetAttributeSetDataValid),
            ([sourceAttributeData, targetAttributeData, targetAttributeSetData]) =>
                applyProjectOperator(sourceAttributeData, targetAttributeData, targetAttributeSetData),
            "applyProjectOperator",
        )

        return {
            evaluatedGeometryData: geometryAttributeSetData,
            invalidator: scope.phi(sourceAttributeDataInvalid, targetAttributeDataInvalid, targetAttributeSetDataInvalid),
        }
    }
}
